// ============================================================================
// AdminEnrollmentsPanel — program enrollments (Build S4, Task #25).
// ============================================================================
// Sidebar: #admin/enrollments (Programs & Enrollments group). LIST (filters)
// + DETAIL (route-driven, S2/S3 precedent). Detail: eligibility checklist,
// status transitions (D-5 — server writes attributed history), attach
// agreement, recompute snapshot.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import {
  Loader2, AlertCircle, ClipboardList, ArrowLeft, CheckCircle2, XCircle,
  RefreshCw, Link2, ArrowRightCircle, MinusCircle, FileSignature,
} from 'lucide-react';
import {
  api, type EnrollmentSummary, type AdminEnrollmentDetail, type EnrollmentStatus,
  type AdminAgreement,
} from '../api';

interface Props {
  token: string;
  enrollmentId?: string;
}

const STATUS_TONES: Record<string, string> = {
  invited: 'bg-gray-100 text-gray-700',
  application_started: 'bg-blue-50 text-blue-700',
  application_submitted: 'bg-blue-100 text-blue-800',
  under_review: 'bg-amber-100 text-amber-800',
  conditionally_approved: 'bg-amber-100 text-amber-800',
  enrolled: 'bg-green-100 text-green-800',
  completed: 'bg-green-200 text-green-900',
  withdrawn: 'bg-gray-200 text-gray-600',
  terminated: 'bg-red-100 text-red-700',
};

const REQUIREMENT_LABELS: Record<string, string> = {
  AD_2047: 'AD-2047 customer data worksheet',
  AD_1026: 'AD-1026 HELC/WC certification',
  W9: 'IRS W-9',
  AMP_AGREEMENT_EXECUTED: 'Executed AMP participation agreement',
  CPA52_SIGNED: 'Signed CPA-52',
  // 048b appended VOC_ASSESSMENT to the AMP program-year eligibility
  // requirements (prod, 2026-07-11), but this map was never extended — so the
  // checklist rendered the raw code "VOC_ASSESSMENT" via the `?? r.requirement_type`
  // fallback in the requirements renderer below. Truthful but unreadable. Label matches
  // documents.document_type.label in prod, as every other entry here does.
  VOC_ASSESSMENT: 'VOC assessment',
};

function StatusPill({ status }: { status: string }) {
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_TONES[status] ?? 'bg-gray-100 text-gray-700'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export function AdminEnrollmentsPanel({ token, enrollmentId }: Props) {
  if (enrollmentId !== undefined) return <EnrollmentDetail token={token} enrollmentId={enrollmentId} />;
  return <EnrollmentList token={token} />;
}

// ============================================================================
// LIST
// ============================================================================
function EnrollmentList({ token }: { token: string }) {
  const [rows, setRows] = useState<EnrollmentSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await api.adminEnrollments(token, statusFilter ? { status: statusFilter } : undefined);
      setRows(res.enrollments);
    } catch (err: any) {
      setError(err?.message ?? 'Could not load enrollments');
    }
  }, [token, statusFilter]);

  useEffect(() => { void load(); }, [load]);

  if (error) {
    return <div className="flex items-center gap-2 text-red-700 bg-red-50 rounded-lg p-4"><AlertCircle className="w-5 h-5 shrink-0" /> {error}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-green-700" />
          <h2 className="text-xl font-bold text-gray-900">Enrollments</h2>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="#admin/enrollments/record-external-batch"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-green-700 rounded-lg hover:bg-green-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-2"
          >
            <FileSignature className="w-4 h-4" /> Record external wave
          </a>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            {Object.keys(STATUS_TONES).map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
      </div>

      {!rows ? (
        <div className="flex items-center gap-2 text-gray-500 p-6"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 bg-white rounded-xl border border-gray-200 p-6">No enrollments match.</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100">
                <th className="p-3">Farm</th>
                <th className="p-3">Program</th>
                <th className="p-3">Status</th>
                <th className="p-3">Cohort</th>
                <th className="p-3">Eligibility</th>
                <th className="p-3">Enrolled</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr
                  key={e.enrollment_id}
                  className="border-b border-gray-50 hover:bg-green-50/40 focus:bg-green-50 cursor-pointer"
                  onClick={() => { window.location.hash = `#admin/enrollments/${e.enrollment_id}`; }}
                  // Copilot PR8: keyboard-accessible row (tab stop + Enter/Space)
                  role="link"
                  tabIndex={0}
                  aria-label={`Open enrollment for ${e.farm_name}`}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault();
                      window.location.hash = `#admin/enrollments/${e.enrollment_id}`;
                    }
                  }}
                >
                  <td className="p-3">
                    <span className="font-mono text-xs text-gray-500 mr-2">{e.farm_code}</span>
                    <span className="font-medium text-gray-900">{e.farm_name}</span>
                  </td>
                  <td className="p-3 text-gray-600">{e.program_code} {e.year_no}</td>
                  <td className="p-3"><StatusPill status={e.status} /></td>
                  <td className="p-3 text-gray-600">{e.cohort_tag ?? '—'}</td>
                  <td className="p-3">
                    {e.snapshot_complete == null ? <span className="text-gray-400">—</span>
                      : e.snapshot_complete
                        ? <span className="inline-flex items-center gap-1 text-green-700 text-xs font-semibold"><CheckCircle2 className="w-4 h-4" /> complete</span>
                        : <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-semibold"><MinusCircle className="w-4 h-4" /> items missing</span>}
                  </td>
                  <td className="p-3 text-gray-600">{e.enrolled_at ? e.enrolled_at.slice(0, 10) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// DETAIL
// ============================================================================
function EnrollmentDetail({ token, enrollmentId }: { token: string; enrollmentId: string }) {
  const [detail, setDetail] = useState<AdminEnrollmentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [transitionTo, setTransitionTo] = useState<EnrollmentStatus | ''>('');
  const [reason, setReason] = useState('');
  const [attaching, setAttaching] = useState(false);
  const [agreements, setAgreements] = useState<AdminAgreement[] | null>(null);
  const [agreementChoice, setAgreementChoice] = useState('');

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await api.adminEnrollmentDetail(token, enrollmentId);
      setDetail(res);
    } catch (err: any) {
      setError(err?.message ?? 'Could not load enrollment');
    }
  }, [token, enrollmentId]);

  useEffect(() => { void load(); }, [load]);

  const doTransition = async () => {
    if (!transitionTo || busy) return;
    setBusy(true); setError(null);
    try {
      await api.adminEnrollmentTransition(token, enrollmentId, transitionTo, reason || null);
      setTransitionTo(''); setReason('');
      await load();
    } catch (err: any) {
      setError(err?.message ?? 'Transition failed');
    } finally {
      setBusy(false);
    }
  };

  const startAttach = async () => {
    setAttaching(true);
    // Copilot PR8 r6: reset before fetching so a cancel/re-open never shows
    // the previous fetch's stale list in place of the loading state.
    setAgreements(null);
    setAgreementChoice('');
    try {
      const res = await api.adminAgreements(token);
      const farmId = detail?.enrollment.farm_id;
      setAgreements(res.agreements.filter((a) => a.farm_id === farmId && !['terminated', 'expired'].includes(a.status)));
    } catch (err: any) {
      setError(err?.message ?? 'Could not load agreements');
      setAttaching(false);
    }
  };

  const doAttach = async () => {
    if (!agreementChoice || busy) return;
    setBusy(true); setError(null);
    try {
      await api.adminEnrollmentAttachAgreement(token, enrollmentId, agreementChoice);
      setAttaching(false); setAgreementChoice('');
      await load();
    } catch (err: any) {
      setError(err?.message ?? 'Attach failed');
    } finally {
      setBusy(false);
    }
  };

  const doRecompute = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await api.adminEnrollmentRecompute(token, enrollmentId);
      await load();
    } catch (err: any) {
      setError(err?.message ?? 'Recompute failed');
    } finally {
      setBusy(false);
    }
  };

  if (!detail && !error) {
    return <div className="flex items-center gap-2 text-gray-500 p-6"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => { window.location.hash = '#admin/enrollments'; }}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4" /> All enrollments
      </button>

      {error && (
        <div className="flex items-center gap-2 text-red-700 bg-red-50 rounded-lg p-3 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {detail && (
        <>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  <span className="font-mono text-sm text-gray-500 mr-2">{detail.enrollment.farm_code}</span>
                  {detail.enrollment.farm_name}
                </h2>
                <div className="mt-1 text-sm text-gray-600">
                  {detail.enrollment.program_name} · {detail.enrollment.year_no}
                  {detail.enrollment.cohort_tag ? ` · ${detail.enrollment.cohort_tag}` : ''}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <StatusPill status={detail.enrollment.status} />
                {!['enrolled', 'withdrawn', 'terminated', 'completed'].includes(detail.enrollment.status) && (
                  <a
                    href={`#admin/enrollments/${enrollmentId}/record-external`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-green-700 rounded-lg hover:bg-green-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-2"
                  >
                    <FileSignature className="w-3.5 h-3.5" /> Record external agreement
                  </a>
                )}
              </div>
            </div>

            {detail.allowed_transitions.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap items-end gap-2">
                <label className="block text-sm">
                  <span className="text-gray-700">Transition to</span>
                  <select
                    value={transitionTo}
                    onChange={(e) => setTransitionTo(e.target.value as EnrollmentStatus | '')}
                    className="mt-1 border border-gray-300 rounded-lg px-3 py-1.5 bg-white block"
                  >
                    <option value="">Choose…</option>
                    {detail.allowed_transitions.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                </label>
                <label className="block text-sm grow max-w-md">
                  <span className="text-gray-700">Reason (recorded in history)</span>
                  <input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-1.5" />
                </label>
                <button
                  type="button"
                  disabled={!transitionTo || busy}
                  onClick={() => void doTransition()}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold text-white bg-green-700 rounded-lg hover:bg-green-800 disabled:opacity-50"
                >
                  <ArrowRightCircle className="w-4 h-4" /> Apply
                </button>
              </div>
            )}
            {detail.enrollment.status === 'under_review' || detail.enrollment.status === 'conditionally_approved' ? (
              <p className="mt-2 text-xs text-gray-500">
                Moving to <b>enrolled</b> requires an attached agreement in <b>executed</b> status — the server enforces both.
              </p>
            ) : null}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Agreement</h3>
              {!detail.agreement && !attaching && (
                <button type="button" onClick={() => void startAttach()} className="inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-900">
                  <Link2 className="w-4 h-4" /> Attach agreement
                </button>
              )}
            </div>
            {detail.agreement ? (
              <div className="mt-2 text-sm text-gray-700">
                <a href={`#admin/agreements/${detail.agreement.agreement_id}`} className="text-green-700 underline">
                  {detail.agreement.template_version}
                </a>{' '}
                · <StatusPill status={detail.agreement.status} />
                {detail.agreement.effective_date ? ` · effective ${detail.agreement.effective_date}` : ''}
              </div>
            ) : attaching ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {agreements === null ? (
                  <span className="text-sm text-gray-500">Loading agreements…</span>
                ) : agreements.length === 0 ? (
                  <span className="text-sm text-gray-500">No live agreements for this farm — create one under Agreements.</span>
                ) : (
                  <>
                    <select value={agreementChoice} onChange={(e) => setAgreementChoice(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white">
                      <option value="">Choose…</option>
                      {agreements.map((a) => (
                        <option key={a.agreement_id} value={a.agreement_id}>{a.template_version} ({a.status})</option>
                      ))}
                    </select>
                    <button type="button" disabled={!agreementChoice || busy} onClick={() => void doAttach()} className="px-3 py-1.5 text-sm font-semibold text-white bg-green-700 rounded-lg disabled:opacity-50">
                      Attach
                    </button>
                    <button type="button" onClick={() => { setAttaching(false); setAgreementChoice(''); }} className="text-sm text-gray-500">Cancel</button>
                  </>
                )}
              </div>
            ) : (
              <p className="mt-2 text-sm text-gray-500">No agreement attached yet.</p>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Eligibility checklist</h3>
              <button
                type="button"
                disabled={busy}
                onClick={() => void doRecompute()}
                className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
              >
                <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> Recompute
              </button>
            </div>
            {!detail.snapshot ? (
              <p className="mt-2 text-sm text-gray-500">No snapshot yet — recompute to generate one.</p>
            ) : (
              <>
                <p className="mt-1 text-xs text-gray-500">
                  Computed {detail.snapshot.computed_at ? detail.snapshot.computed_at.slice(0, 19).replace('T', ' ') : '—'} UTC ·{' '}
                  {detail.snapshot.is_complete
                    ? <span className="text-green-700 font-semibold">COMPLETE</span>
                    : <span className="text-amber-700 font-semibold">items outstanding</span>}
                </p>
                <ul className="mt-3 space-y-2">
                  {detail.snapshot.requirements.map((r) => (
                    <li key={r.requirement_type} className="flex items-start gap-2 text-sm">
                      {!r.applies ? (
                        <MinusCircle className="w-4 h-4 text-gray-300 mt-0.5 shrink-0" />
                      ) : r.is_satisfied ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      )}
                      <span className={r.applies ? 'text-gray-800' : 'text-gray-400'}>
                        {REQUIREMENT_LABELS[r.requirement_type] ?? r.requirement_type}
                        {!r.applies && <span className="ml-1 text-xs italic">(not required this year{r.note ? ` — ${r.note}` : ''})</span>}
                        {r.applies && r.satisfying_document_id && (
                          <a href={`#admin/documents/${r.satisfying_document_id}`} className="ml-2 text-xs text-green-700 underline">view document</a>
                        )}
                        {r.applies && r.satisfying_agreement_id && (
                          <a href={`#admin/agreements/${r.satisfying_agreement_id}`} className="ml-2 text-xs text-green-700 underline">view agreement</a>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <h3 className="font-semibold text-gray-900">Status history</h3>
            {detail.history.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">No history.</p>
            ) : (
              <ul className="mt-2 space-y-1.5">
                {detail.history.map((h) => (
                  <li key={h.history_id} className="text-sm text-gray-700">
                    <span className="text-gray-400 font-mono text-xs mr-2">
                      {h.transitioned_at ? h.transitioned_at.slice(0, 16).replace('T', ' ') : '—'}
                    </span>
                    {h.from_status ? `${h.from_status.replace(/_/g, ' ')} → ` : 'created as '}
                    <b>{h.to_status.replace(/_/g, ' ')}</b>
                    {h.reason ? <span className="text-gray-500"> — {h.reason}</span> : null}
                    {h.transitioned_by == null && h.from_status !== null && (
                      <span className="ml-1 text-xs text-gray-400 italic">(system)</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
